import { Mail, Phone, MapPin, Copy, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { SITE, IMAGES } from "@/lib/site";

export const Contact = () => {
  const copyEmail = () => {
    navigator.clipboard?.writeText(SITE.email);
    toast.success("Email address copied!");
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#0B2545] overflow-hidden"
    >
      <img
        src={IMAGES.warehouse}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover opacity-10"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B2545] via-[#0B2545]/95 to-[#0B2545]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <Reveal className="lg:col-span-6">
            <span className="font-mono text-xs font-medium tracking-[0.2em] uppercase text-amber-400">
              Get In Touch
            </span>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.1]">
              Let&apos;s discuss your supply &amp; import needs
            </h2>
            <p className="mt-5 text-base sm:text-lg text-blue-100/80 leading-relaxed max-w-lg">
              Contact our team directly by email or phone to discuss your
              supply, import, and sourcing requirements.
            </p>
            <a
              href={SITE.mailto}
              data-testid="contact-section-email-us-button"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-amber-500 px-8 py-4 text-sm font-semibold text-[#0B2545] shadow-lg shadow-amber-500/30 transition-all duration-200 hover:bg-amber-400 hover:-translate-y-0.5 active:scale-95 focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B2545]"
            >
              <Mail className="h-4 w-4" /> Email Us Now
            </a>
          </Reveal>

          <Reveal className="lg:col-span-6" delay={0.15}>
            <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-6 sm:p-8 space-y-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-white/10">
                    <Mail className="h-5 w-5 text-amber-400" />
                  </span>
                  <div>
                    <p className="text-xs font-mono uppercase tracking-wider text-amber-400">
                      Email
                    </p>
                    <a
                      href={SITE.mailto}
                      data-testid="contact-email-link"
                      className="mt-1 block text-white font-medium hover:text-amber-300 transition-colors break-all"
                    >
                      {SITE.email}
                    </a>
                  </div>
                </div>
                <button
                  onClick={copyEmail}
                  data-testid="contact-copy-email-button"
                  aria-label="Copy email address"
                  className="flex-none rounded-lg p-2 text-amber-300 hover:bg-white/10 transition-colors"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </div>

              <div className="h-px bg-white/10" />

              <div className="flex items-start gap-4">
                <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-white/10">
                  <Phone className="h-5 w-5 text-amber-400" />
                </span>
                <div>
                  <p className="text-xs font-mono uppercase tracking-wider text-amber-400">
                    Phone
                  </p>
                  <div className="mt-1 flex flex-col gap-1">
                    {SITE.phones.map((num, i) => (
                      <a
                        key={num}
                        href={`tel:${num}`}
                        data-testid={`contact-phone-${i + 1}`}
                        className="text-white font-medium hover:text-amber-300 transition-colors"
                      >
                        {num}
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              <div className="h-px bg-white/10" />

              <div className="flex items-start gap-4">
                <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-white/10">
                  <MapPin className="h-5 w-5 text-amber-400" />
                </span>
                <div>
                  <p className="text-xs font-mono uppercase tracking-wider text-amber-400">
                    Address
                  </p>
                  <p
                    data-testid="contact-address"
                    className="mt-1 text-white font-medium leading-relaxed"
                  >
                    {SITE.address.street}
                    <br />
                    {SITE.address.city}, {SITE.address.country}
                  </p>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      `${SITE.address.street}, ${SITE.address.city}, ${SITE.address.country}`
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-map-link"
                    className="mt-2 inline-flex items-center gap-1 text-sm text-amber-400 hover:text-amber-300 transition-colors"
                  >
                    View on map <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2} className="mt-10 lg:mt-14">
          <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl ring-1 ring-white/5">
            <iframe
              title="Top Service location — Wardian, Alexandria"
              data-testid="contact-map-embed"
              src="https://www.openstreetmap.org/export/embed.html?bbox=29.8500%2C31.1750%2C29.8850%2C31.1970&layer=mapnik&marker=31.1860%2C29.8670"
              className="w-full h-[320px] sm:h-[400px] border-0 grayscale-[0.1]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
};
